import { FC, useEffect, PropsWithChildren } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import {
    BannerNotification,
    Column,
    GlobalStyles,
    LoadingOverlay,
    Row,
    styled,
    ThemeProvider,
    Toaster,
    ToastTypes,
} from '@streem/ui-react';
import { audio } from '@streem/sdk-react';
import { ErrorBoundary } from './util/error/error_boundary';
import { Observer } from 'mobx-react';
import { autorun } from 'mobx';
import { GlobalStoreProvider, useInitializeGlobalStores } from './hooks/use_global_context';
import UnavailablePage from './pages/authentication/unavailable_page';
import { BaseRoutes } from './routing/routes';
import { MaybeRedirectAnonymousSharedRoute } from './routing/share-link-redirects';
import { VerticalNavBar } from './components/vertical_nav_bar/vertical_nav_bar';
import { TopNavBar } from './components/top_nav_bar/top_nav_bar';
import { InCallListener } from './components/calls/in_call_listener';
import { AnonymousRoute } from './routing/anonymous_route';
import { LoginPage } from './pages/authentication/login_page';
import { NotFoundRoute } from './pages/authentication/not_found_page';
import { ProtectedRoute } from './routing/protected_route';
import { TermsAndConditionsPage } from './pages/authentication/terms_and_conditions_page';
import { DeviceUpdateNotification } from './components/device_update_notification';
import { SurveyForm } from './components/calls/survey_form';
import config from './config';
import { RolesAuth } from './components/authorization/roles_auth';
import { VersionCheck } from './components/version_check';
import { InactivityMonitor } from './components/inactivity_monitor';
import { ForgotPasswordPage } from './pages/authentication/forgot_password_page';
import { createGetCompanyStore, GetCompanyProvider } from './hooks/detail_store_hooks';
import { CookiesDisabledPage } from './pages/authentication/cookies_disabled_page';
import {
    createGetCompanyCallLogStore,
    createGetUserCallLogStore,
    GetCompanyCallLogProvider,
    GetUserCallLogProvider,
} from './hooks/list_store_hooks';
import { IFrameLogoutMonitor } from './components/iframe_logout_monitor';
import { SessionExpiredRoute } from './pages/authentication/session_expired_route';
import appLogger from './util/logging/app_logger';
import { AllowNotificationsModalOnce } from './components/allow_notifications/allow_notifications_modal';
import { getPermissionSettings } from './util/browser_notification_settings';
import { logPreferredVideoDevice } from './util/log_preferred_video_device';
import { useLoadRoomOutcomeFormConfig } from './hooks/use_room_outcome_form_config';
import { useInitializeDeviceStore } from './hooks/use_initialize_device_store';

const App: FC<PropsWithChildren> = () => {
    const globalStores = useInitializeGlobalStores();
    const { authStore, configStore, companySettingsStore, uiStore, sdkStore } = globalStores;
    const companyStore = createGetCompanyStore();
    const companyCallLogStore = createGetCompanyCallLogStore();
    const userCallLogStore = createGetUserCallLogStore();

    useLoadRoomOutcomeFormConfig(companySettingsStore, uiStore);

    useEffect(() => {
        if (!authStore.initialized || configStore.loading || configStore.lastError) return;
        const promises = [companyStore.fetch(config.companyCode)];
        return () => promises.forEach(p => p.cancel());
    }, [companyStore, authStore.initialized, configStore.loading, configStore.lastError]);

    useEffect(() => {
        const handleAudioOutputDeviceChanged = (audioOutputDevice: MediaDeviceInfo) => {
            // do not show toast while on a call
            if (!sdkStore.roomSession?.current) {
                uiStore.addToastWithManualClose({
                    content: `Speaker changed to ${audioOutputDevice.label}`,
                    flavor: ToastTypes.INFO,
                    id: `audio-output-changed`,
                });
            }
        };

        const handleAudioInputDeviceChanged = (audioInputDevice: MediaDeviceInfo) => {
            // do not show toast while on a call
            if (!sdkStore.roomSession?.current) {
                uiStore.addToastWithManualClose({
                    content: `Microphone changed to ${audioInputDevice.label}`,
                    flavor: ToastTypes.INFO,
                    id: `audio-input-changed`,
                });
            }
        };

        const handleRingtonePlaying = async () => {
            // handle race condition:
            //    if expert is on call, but ringtone is still playing, then call pause.
            if (sdkStore.roomSession?.current && audio.ringtoneAudioPaused === false) {
                appLogger.info(`Expert already on call. Pausing ringtone again.`);
                await audio.pauseRingtoneAudio();
            }
        };

        audio.initialize(handleRingtonePlaying);
        audio.subscribeToAudioOutputDeviceChanged(handleAudioOutputDeviceChanged);
        audio.subscribeToAudioInputDeviceChanged(handleAudioInputDeviceChanged);

        return () => {
            audio.unsubscribeToAudioOutputDeviceChanged(handleAudioOutputDeviceChanged);
            audio.unsubscribeToAudioInputDeviceChanged(handleAudioInputDeviceChanged);
            appLogger.info(`Unsubscribed to handleAudioDeviceChanged`);
        };
    }, [uiStore, sdkStore]);

    // Heartbeat log every 5min
    useEffect(() => {
        const timer = setInterval(() => {
            appLogger.info(
                `Heartbeat, local time: ${new Date().toISOString()}, window state: ${
                    document.visibilityState
                }`,
            );
        }, 1000 * 60 * 5);

        return () => clearInterval(timer);
    }, []);

    /**
     * Wait for userId to exist on auth store, then fetch company settings
     * using 'autorun' which is provided by mobx for when a hook depends on an observable
     */
    useEffect(() => {
        autorun(() => {
            if (!authStore.userId) return;
            const promise = companySettingsStore.loadSettings();
            return () => promise.cancel();
        });
    }, [authStore.userId, companySettingsStore]);

    /**
     * Wait for userId to exist on auth store, then check notification permission
     * and log it.
     */
    useEffect(() => {
        autorun(() => {
            if (!authStore.userId) return;
            appLogger.info(
                `Web Browser Notification permissionStatus=${getPermissionSettings()} for user ${
                    authStore.userId
                }.`,
            );
            logPreferredVideoDevice();
        });
    }, [authStore.userId]);

    useInitializeDeviceStore(authStore);

    return (
        <ErrorBoundary>
            <ThemeProvider>
                <GlobalStoreProvider value={globalStores}>
                    <GetCompanyProvider value={companyStore}>
                        <GetCompanyCallLogProvider value={companyCallLogStore}>
                            <GetUserCallLogProvider value={userCallLogStore}>
                                <GlobalStyles />
                                <BrowserRouter>
                                    <Observer>
                                        {() => {
                                            if (!window.navigator.cookieEnabled) {
                                                return <CookiesDisabledPage />;
                                            }
                                            if (!authStore.initialized || configStore.loading) {
                                                return <LoadingOverlay />;
                                            }
                                            if (
                                                configStore.lastError ||
                                                companySettingsStore.lastError
                                            ) {
                                                return <UnavailablePage />;
                                            }

                                            return (
                                                <>
                                                    <Switch>
                                                        <AnonymousRoute
                                                            path={'/login'}
                                                            exact={true}
                                                            component={LoginPage}
                                                        />
                                                        <AnonymousRoute
                                                            path="/forgot"
                                                            exact={true}
                                                            component={ForgotPasswordPage}
                                                        />
                                                        <Route
                                                            path="/404"
                                                            exact={true}
                                                            component={NotFoundRoute}
                                                        />
                                                        <Route
                                                            path="/session-expired"
                                                            exact={true}
                                                            component={SessionExpiredRoute}
                                                        />
                                                        <Route
                                                            path={[
                                                                '/share/:token/details/:roomSid',
                                                                '/share/:token/details/:roomSid/artifact/:artifactSid',
                                                            ]}
                                                            component={
                                                                MaybeRedirectAnonymousSharedRoute
                                                            }
                                                            exact={true}
                                                        />
                                                        <ProtectedRoute
                                                            path="/termsandconditions"
                                                            exact={true}
                                                            component={TermsAndConditionsPage}
                                                            only={[
                                                                'SUPER_ADMIN',
                                                                'COMPANY_ADMIN',
                                                                'AGENT',
                                                            ]}
                                                        />
                                                        <Route>
                                                            <AppWrapper>
                                                                <TopNavBar />
                                                                <SideBarAndMainSectionWrapper>
                                                                    <VerticalNavBar />
                                                                    <MainSectionWrapper data-testid="main-section-wrapper">
                                                                        <BannerNotification
                                                                            content={
                                                                                uiStore.bannerNotificationContent
                                                                            }
                                                                        />
                                                                        <BaseRoutes />
                                                                    </MainSectionWrapper>
                                                                    <RolesAuth except="ANONYMOUS">
                                                                        <SurveyForm />
                                                                        <InCallListener />
                                                                        <InactivityMonitor />
                                                                    </RolesAuth>
                                                                    {sdkStore.userSession
                                                                        .current && (
                                                                        <DeviceUpdateNotification
                                                                            currentUserSession={
                                                                                sdkStore.userSession
                                                                                    .current
                                                                            }
                                                                        />
                                                                    )}
                                                                    <IFrameLogoutMonitor />
                                                                </SideBarAndMainSectionWrapper>
                                                            </AppWrapper>
                                                        </Route>
                                                    </Switch>
                                                    <Toaster
                                                        content={uiStore.toasts.slice()}
                                                        onClose={toastId =>
                                                            uiStore.removeToast(toastId)
                                                        }
                                                        onPauseToastTimeout={toastId =>
                                                            uiStore.pauseRemoveToast(toastId)
                                                        }
                                                        onResumeToastTimeout={(toastId, timeout) =>
                                                            uiStore.removeToastViaTimeout(
                                                                toastId,
                                                                timeout,
                                                            )
                                                        }
                                                    />
                                                    <VersionCheck
                                                        roomId={
                                                            sdkStore.roomSession?.current?.roomId
                                                        }
                                                    />
                                                    <AllowNotificationsModalOnce />
                                                </>
                                            );
                                        }}
                                    </Observer>
                                </BrowserRouter>
                            </GetUserCallLogProvider>
                        </GetCompanyCallLogProvider>
                    </GetCompanyProvider>
                </GlobalStoreProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
};

export default App;

const MainSectionWrapper = styled.div({
    flex: 1,
    overflowY: 'auto',
});

const SideBarAndMainSectionWrapper = styled(Row)({
    overflow: 'hidden',
    flex: 1,
});

const AppWrapper = styled(Column)({
    height: '100vh',
});

AppWrapper.displayName = 'AppWrapper';
